import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { FilterChips } from '@/components/data-table/filters-v2/filter-chips';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

import { FilterAccess, SavedFilter, TableFilterConfig, TableFilterState } from '../types/types';

interface SaveFiltersModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (filter: Omit<SavedFilter, 'id' | 'createdAt' | 'createdBy'>) => void;
  filtersConfig: TableFilterConfig[];
  activeFilters: TableFilterState;
  existingFilter?: SavedFilter;
}

/**
 * Modal for saving or editing filter presets
 */
export function SaveFiltersModal({
  open,
  onClose,
  onSave,
  filtersConfig,
  activeFilters,
  existingFilter,
}: SaveFiltersModalProps) {
  const { t } = useTranslation('common');
  const isEditMode = !!existingFilter;

  const [name, setName] = useState(existingFilter?.name || '');
  const [access, setAccess] = useState<FilterAccess>(existingFilter?.access || FilterAccess.Public);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      await onSave({
        name: name.trim(),
        filters: activeFilters,
        access,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setAccess(FilterAccess.Public);
    onClose();
  };

  // Empty handler for read-only chips display
  const noop = () => {};

  return (
    <Modal open={open} onOpen={() => {}} onClose={handleClose} className="max-w-md">
      <Modal.Title>
        {isEditMode ? t('tableFilters.editFilters') : t('tableFilters.saveNewFilters')}
      </Modal.Title>

      <Modal.Body className="space-y-4 pt-0">
        {/* Used filters preview */}
        <div className="space-y-2">
          <Label className="text-sm">{t('tableFilters.usedFilters')}</Label>
          <div className="flex flex-wrap items-center gap-2">
            <FilterChips
              filtersConfig={filtersConfig}
              activeFilters={activeFilters}
              onRemoveFilter={noop}
              showClearButton={false}
            />
          </div>
        </div>

        {/* Filter name input */}
        <div className="space-y-2">
          <Label htmlFor="filter-name">
            {t('tableFilters.name')}
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <span className="text-destructive">*</span>
          </Label>
          <Input
            id="filter-name"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('tableFilters.enterFilterName')}
            autoFocus
          />
        </div>

        {/* Access toggle */}
        <div className="space-y-2">
          <Label>{t('tableFilters.access')}</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={access === FilterAccess.Public ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAccess(FilterAccess.Public)}
              className={cn(
                'rounded-full',
                access === FilterAccess.Public && 'pointer-events-none'
              )}
            >
              {t('tableFilters.forEveryone')}
            </Button>
            <Button
              type="button"
              variant={access === FilterAccess.Private ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAccess(FilterAccess.Private)}
              className={cn(
                'rounded-full',
                access === FilterAccess.Private && 'pointer-events-none'
              )}
            >
              {t('tableFilters.onlyForMe')}
            </Button>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
          {t('cancel')}
        </Button>
        <Button onClick={handleSave} disabled={!name.trim() || isSubmitting}>
          {t('save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
